import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from './Button';

interface CustomSplitConfig {
  customSplitUnit: 'percentage' | 'fixed';
  customReferencePartner: 'partner1' | 'partner2';
  /** La valeur active : pourcentage (0-100) ou montant fixe en € */
  value: string;
}

interface CustomSplitConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: CustomSplitConfig) => void;
  /** Configuration actuelle */
  currentUnit: 'percentage' | 'fixed';
  currentReferencePartner: 'partner1' | 'partner2';
  currentPercentage: string;
  currentFixedAmount: string;
  /** Montant total de l'opération (pour conversion %/€) */
  operationAmount: number;
  partner1Name: string;
  partner2Name: string;
}

export default function CustomSplitConfigModal({
  isOpen,
  onClose,
  onSave,
  currentUnit,
  currentReferencePartner,
  currentPercentage,
  currentFixedAmount,
  operationAmount,
  partner1Name,
  partner2Name,
}: CustomSplitConfigModalProps) {
  const { t } = useTranslation('operations');
  const [unit, setUnit] = useState<'percentage' | 'fixed'>(currentUnit);
  const [referencePartner, setReferencePartner] = useState<'partner1' | 'partner2'>(currentReferencePartner);
  const [value, setValue] = useState('');

  // Initialiser depuis les props à chaque ouverture
  useEffect(() => {
    if (isOpen) {
      setUnit(currentUnit);
      setReferencePartner(currentReferencePartner);
      setValue(currentUnit === 'fixed' ? currentFixedAmount : currentPercentage);
    }
  }, [isOpen, currentUnit, currentReferencePartner, currentPercentage, currentFixedAmount]);

  const handleUnitToggle = (newUnit: 'percentage' | 'fixed') => {
    if (newUnit === unit) return;
    // Convertir la valeur
    if (newUnit === 'fixed') {
      // % → €
      const pct = parseFloat(value) || 0;
      const fixedVal = operationAmount > 0 ? ((pct / 100) * operationAmount).toFixed(2) : '0';
      setValue(fixedVal);
    } else {
      // € → %
      const fixed = parseFloat(value) || 0;
      const pctVal = operationAmount > 0 ? ((fixed / operationAmount) * 100).toFixed(1) : '0';
      setValue(pctVal);
    }
    setUnit(newUnit);
  };

  const handleSave = () => {
    onSave({
      customSplitUnit: unit,
      customReferencePartner: referencePartner,
      value,
    });
    onClose();
  };

  if (!isOpen) return null;

  const refName = referencePartner === 'partner1' ? partner1Name : partner2Name;
  const otherName = referencePartner === 'partner1' ? partner2Name : partner1Name;

  // Calcul du résumé
  const numValue = parseFloat(value) || 0;
  let refAmount: number;
  let otherAmount: number;
  let refPct: number;
  let otherPct: number;

  if (unit === 'fixed') {
    refAmount = Math.min(numValue, operationAmount);
    otherAmount = Math.max(0, operationAmount - refAmount);
    refPct = operationAmount > 0 ? (refAmount / operationAmount) * 100 : 0;
    otherPct = operationAmount > 0 ? (otherAmount / operationAmount) * 100 : 0;
  } else {
    refPct = Math.min(numValue, 100);
    otherPct = 100 - refPct;
    refAmount = operationAmount > 0 ? (refPct / 100) * operationAmount : 0;
    otherAmount = operationAmount > 0 ? (otherPct / 100) * operationAmount : 0;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('customSplit.title')}
          </h3>

          {/* Partenaire de référence */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('customSplit.referencePartner')}
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setReferencePartner('partner1')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  referencePartner === 'partner1'
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {partner1Name}
              </button>
              <button
                type="button"
                onClick={() => setReferencePartner('partner2')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  referencePartner === 'partner2'
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {partner2Name}
              </button>
            </div>
          </div>

          {/* Mode (% ou €) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('customSplit.splitMode')}
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleUnitToggle('percentage')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  unit === 'percentage'
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('customSplit.percentage')}
              </button>
              <button
                type="button"
                onClick={() => handleUnitToggle('fixed')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  unit === 'fixed'
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('customSplit.fixedAmount')}
              </button>
            </div>
          </div>

          {/* Valeur */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {unit === 'percentage' ? t('customSplit.partnerSharePercent', { name: refName }) : t('customSplit.partnerShareFixed', { name: refName })}
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max={unit === 'percentage' ? 100 : undefined}
                step={unit === 'fixed' ? '0.01' : '1'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="input w-full pr-8 text-sm"
                placeholder={unit === 'percentage' ? '50' : '0.00'}
                autoFocus
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                {unit === 'fixed' ? '€' : '%'}
              </span>
            </div>
          </div>

          {/* Résumé */}
          <div className="mb-6 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">{t('customSplit.summary')}</p>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{refName}</span>
                <span className="font-medium text-gray-900">
                  {refAmount.toFixed(2)} € ({refPct.toFixed(1)}%)
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{otherName}</span>
                <span className="font-medium text-gray-900">
                  {otherAmount.toFixed(2)} € ({otherPct.toFixed(1)}%)
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('cancel', { ns: 'common' })}
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave}>
              {t('customSplit.apply')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
